/**
 * AppLayout.tsx — The shell for all logged-in pages.
 *
 * Extracted from the original App.tsx. Preserves all behavior:
 *   - Sticky sidebar with nav groups + admin filtering
 *   - Mobile sidebar w/ overlay + hamburger
 *   - Pending review + demo count badges (auto-fetched)
 *   - Profile dropdown with mode switching + "View as Client"
 *   - "Viewing as: X" banner when in client view
 *   - Atlas AI drawer
 *   - Global toast container
 *
 * Replaces: the inline Sidebar, ProfileAvatar, DashboardLayout in App.tsx.
 *
 * Router usage in App.tsx:
 *   <Route element={<AppLayout />}>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *     ...
 *   </Route>
 */

import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin, AppMode } from '../../AdminContext';
import { useBreakpoint } from '../../hooks/useMediaQuery';
import Atlas from '../Atlas';
import { Icon } from '../ui/Icons';
import { ToastContainer } from '../ui/Toast';

/* ═══════════════ NAV CONFIG ═══════════════ */

interface NavItemConfig {
  to: string;
  label: string;
  icon: keyof typeof Icon;
  badgeKey?: 'pendingReview' | 'demoRequests';
  adminOnly?: boolean;
  clientOnly?: boolean; // only visible when NOT in admin mode
}
interface NavGroupConfig {
  label?: string;
  adminOnly?: boolean;
  items: NavItemConfig[];
}

const navGroups: NavGroupConfig[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/dashboard',            label: 'Dashboard',           icon: 'Dashboard' },
      { to: '/my-records',           label: 'My Records',          icon: 'Rows',     clientOnly: true },
      { to: '/matched-outlets',      label: 'Matched Outlets',     icon: 'Check' },
      { to: '/census-database',      label: 'Census Database',     icon: 'Database', adminOnly: true },
      { to: '/review-queue',         label: 'Review Queue',        icon: 'Clock',    badgeKey: 'pendingReview' },
      { to: '/distribution-gaps',    label: 'Distribution Gaps',   icon: 'Chart' },
      { to: '/market-intelligence',  label: 'Market Intelligence', icon: 'Activity' },
      { to: '/registry-map',         label: 'Registry Map',        icon: 'Map' },
      { to: '/reports',              label: 'Reports',             icon: 'FileText' },
    ],
  },
  {
    label: 'Admin',
    adminOnly: true,
    items: [
      { to: '/upload',           label: 'Upload & Map',    icon: 'Upload',    adminOnly: true },
      { to: '/admin-tools',      label: 'Admin Tools',     icon: 'Tool',      adminOnly: true },
      { to: '/clients',          label: 'Clients',         icon: 'Briefcase', adminOnly: true },
      { to: '/client-records',   label: 'Client Records',  icon: 'FileText',  adminOnly: true },
      { to: '/demo-requests',    label: 'Demo Requests',   icon: 'Inbox',     adminOnly: true, badgeKey: 'demoRequests' },
      { to: '/user-management',  label: 'User Management', icon: 'Users',     adminOnly: true },
      { to: '/audit-log',        label: 'Audit Log',       icon: 'Shield',    adminOnly: true },
    ],
  },
];

/* ═══════════════ SIDEBAR ═══════════════ */

const SIDEBAR_WIDTH = 260;

function Sidebar({
  isMobile, sidebarOpen, onClose,
}: {
  isMobile: boolean;
  sidebarOpen: boolean;
  onClose: () => void;
}) {
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [demoCount, setDemoCount] = useState<number | null>(null);
  const { adminMode, modeVersion } = useAdmin();

  useEffect(() => {
    const controller = new AbortController();
    setPendingCount(null);
    setDemoCount(null);
    api.get('/api/stats', { signal: controller.signal })
      .then(r => setPendingCount(r.data.pending_review ?? 0))
      .catch(err => { if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') setPendingCount(0); });
    api.get('/api/demo/count', { signal: controller.signal })
      .then(r => setDemoCount(r.data.count ?? 0))
      .catch(err => { if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') setDemoCount(0); });
    return () => controller.abort();
  }, [modeVersion]);

  const badgeValues = { pendingReview: pendingCount, demoRequests: demoCount };

  const visibleGroups = navGroups
    .filter(g => !g.adminOnly || adminMode)
    .map(g => ({
      ...g,
      items: g.items.filter(i => {
        if (i.adminOnly && !adminMode) return false;
        if (i.clientOnly && adminMode) return false;
        return true;
      }),
    }))
    .filter(g => g.items.length > 0);

  return (
    <nav style={{
      width: SIDEBAR_WIDTH,
      minWidth: SIDEBAR_WIDTH,
      background: theme.surface.panel,
      borderRight: `1px solid ${theme.borderColor.subtle}`,
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      top: 0, left: 0, bottom: 0,
      overflowY: 'auto',
      ...(isMobile ? {
        zIndex: 200,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: `transform ${theme.motion.slow}`,
      } : { zIndex: theme.z.nav }),
    }}>

      {/* Brand */}
      <div style={{
        padding: `${theme.space[5]} ${theme.space[4]} ${theme.space[4]}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.space[2],
      }}>
        <img
          src="/logo-transparent.png"
          alt="Meridian Registry"
          style={{ width: '100%', maxWidth: 180, height: 'auto', display: 'block' }}
        />
        <div style={{
          fontSize: '9px',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          fontFamily: theme.font.ui,
          fontWeight: theme.fontWeight.semibold,
          textAlign: 'center',
          lineHeight: 1,
          marginTop: -4,
        }}>
          <span style={{ color: theme.text.tertiary }}>Where retail meets </span>
          <span style={{ color: theme.brand.cyan }}>intelligence</span>
        </div>
      </div>

      {/* Nav groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.space[6], paddingTop: theme.space[4] }}>
        {visibleGroups.map((group, gi) => (
          <div key={gi}>
            {group.label && (
              <div style={{
                fontSize: 10,
                fontWeight: theme.fontWeight.semibold,
                letterSpacing: theme.letterSpacing.wide,
                textTransform: 'uppercase',
                color: theme.text.quaternary,
                padding: `0 ${theme.space[6]}`,
                marginBottom: theme.space[2],
              }}>
                {group.label}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {group.items.map(item => (
                <NavRow
                  key={item.to}
                  item={item}
                  badge={item.badgeKey ? badgeValues[item.badgeKey] : null}
                  onClick={() => isMobile && onClose()}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1 }} />
    </nav>
  );
}

function NavRow({
  item, badge, onClick,
}: {
  item: NavItemConfig;
  badge: number | null;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  const IconCmp = Icon[item.icon];

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.space[3],
        padding: `9px ${theme.space[6]}`,
        fontSize: theme.fontSize.base,
        fontWeight: isActive ? theme.fontWeight.semibold : theme.fontWeight.medium,
        textDecoration: 'none',
        color: isActive ? theme.text.primary : hover ? theme.text.primary : theme.text.secondary,
        borderLeft: `2px solid ${isActive ? theme.brand.cyan : 'transparent'}`,
        background: isActive
          ? `linear-gradient(90deg, ${theme.brand.cyanSoft}, transparent 70%)`
          : hover ? 'rgba(255,255,255,0.02)' : 'transparent',
        transition: `all ${theme.motion.fast}`,
        fontFamily: theme.font.ui,
      })}
    >
      {({ isActive }: { isActive: boolean }) => (
        <>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            color: isActive ? theme.brand.cyan : theme.text.tertiary,
            flexShrink: 0,
          }}>
            <IconCmp size={16} />
          </span>
          <span style={{ flex: 1 }}>{item.label}</span>
          {badge != null && badge > 0 && (
            <span style={{
              fontSize: 10,
              fontFamily: theme.font.data,
              color: theme.semantic.warning,
              background: theme.semantic.warningSoft,
              border: `1px solid ${theme.semantic.warningBorder}`,
              padding: '1px 6px',
              borderRadius: 4,
              fontWeight: theme.fontWeight.semibold,
            }}>
              {badge.toLocaleString()}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ═══════════════ PROFILE AVATAR ═══════════════ */
/**
 * Upgraded from the original: subtle dropdown styling, proper icons instead of
 * characters, cleaner active states, labeled sections.
 */

function ProfileAvatar() {
  const [open, setOpen] = useState(false);
  const [showClients, setShowClients] = useState(false);
  const [clients, setClients] = useState<string[]>([]);
  const [atlasOpen, setAtlasOpen] = useState(() => {
    try { return localStorage.getItem('atlas_open') === 'true'; } catch { return false; }
  });
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { mode, setMode, logout, viewAsClient, setViewAsClient, modeVersion, isSuperAdmin } = useAdmin();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowClients(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ open: boolean }>).detail;
      setAtlasOpen(detail.open);
      if (detail.open) { setOpen(false); setShowClients(false); }
    };
    window.addEventListener('atlas:toggle', handler);
    return () => window.removeEventListener('atlas:toggle', handler);
  }, []);

  useEffect(() => { setClients([]); }, [modeVersion]);

  useEffect(() => {
    if (showClients && clients.length === 0) {
      api.get<string[]>('/api/clients').then(r => setClients(r.data)).catch(() => {});
    }
  }, [showClients, clients.length]);

  const modes: { key: AppMode; label: string; icon: keyof typeof Icon }[] = [
    { key: 'admin', label: 'Admin Mode', icon: 'Shield' },
    { key: 'demo',  label: 'Demo Mode',  icon: 'Eye' },
  ];

  // Render via portal into document.body so no ancestor (e.g. anything with
  // transform / filter / will-change further up the tree) can scope our
  // position: fixed and let the avatar scroll with the page content.
  return createPortal(
    <div ref={ref} style={{ position: 'fixed', top: 14, right: 20, zIndex: theme.z.dropdown, display: atlasOpen ? 'none' : undefined }}>
      <button
        onClick={() => { setOpen(!open); setShowClients(false); }}
        style={{
          width: 36, height: 36, borderRadius: '50%',
          background: open ? theme.brand.cyan : theme.brand.cyanSoft,
          border: `1px solid ${theme.brand.cyan}`,
          color: open ? theme.text.inverse : theme.brand.cyan,
          fontFamily: theme.font.data,
          fontSize: theme.fontSize.sm,
          fontWeight: theme.fontWeight.bold,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: `all ${theme.motion.fast}`,
          boxShadow: open ? theme.shadow.glow : 'none',
        }}
        aria-label="Account menu"
      >
        MR
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 44, right: 0,
          width: 240,
          background: theme.surface.elevated,
          border: `1px solid ${theme.borderColor.default}`,
          borderRadius: theme.radius.md,
          overflow: 'hidden',
          boxShadow: theme.shadow.modal,
          maxHeight: 440,
          overflowY: 'auto',
        }}>
          <DropItem
            icon={<Icon.User size={14} />}
            onClick={() => { navigate('/account'); setOpen(false); }}
          >
            My Account
          </DropItem>

          {isSuperAdmin && (
            <>
              <SectionLabel>Mode</SectionLabel>
              {modes.map(m => {
                const IconCmp = Icon[m.icon];
                return (
                  <DropItem
                    key={m.key}
                    icon={<IconCmp size={14} />}
                    active={mode === m.key && !viewAsClient}
                    onClick={() => { setMode(m.key); setViewAsClient(null); setOpen(false); setShowClients(false); }}
                  >
                    {m.label}
                  </DropItem>
                );
              })}

              <SectionLabel>View as Client</SectionLabel>
              <DropItem
                icon={<Icon.Users size={14} />}
                onClick={() => setShowClients(!showClients)}
                active={!!viewAsClient}
                rightSlot={showClients ? <Icon.ChevronUp size={12} /> : <Icon.ChevronDown size={12} />}
              >
                {viewAsClient || 'Select client…'}
              </DropItem>
              {showClients && (
                <div style={{ background: theme.surface.inset, maxHeight: 200, overflowY: 'auto' }}>
                  {clients.length === 0 ? (
                    <div style={{ padding: `${theme.space[3]} ${theme.space[4]}`, color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
                      Loading…
                    </div>
                  ) : clients.map(c => (
                    <DropItem
                      key={c}
                      nested
                      active={viewAsClient === c}
                      onClick={() => { setViewAsClient(c); setOpen(false); setShowClients(false); }}
                    >
                      {c}
                    </DropItem>
                  ))}
                </div>
              )}
            </>
          )}

          <div style={{ height: 1, background: theme.borderColor.subtle, margin: `${theme.space[1]} 0` }} />

          <DropItem
            icon={<Icon.LogOut size={14} />}
            danger
            onClick={() => { logout(); navigate('/login'); setOpen(false); }}
          >
            Log out
          </DropItem>
        </div>
      )}
    </div>,
    document.body,
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: `${theme.space[3]} ${theme.space[4]} 6px`,
      fontSize: 10,
      fontWeight: theme.fontWeight.semibold,
      letterSpacing: theme.letterSpacing.wide,
      textTransform: 'uppercase',
      color: theme.text.quaternary,
      borderTop: `1px solid ${theme.borderColor.subtle}`,
      marginTop: 4,
    }}>
      {children}
    </div>
  );
}

function DropItem({
  onClick, children, active, danger, nested, icon, rightSlot,
}: {
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
  danger?: boolean;
  nested?: boolean;
  icon?: React.ReactNode;
  rightSlot?: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  const color = danger ? theme.semantic.danger : active ? theme.brand.cyan : theme.text.secondary;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: theme.space[3],
        width: '100%',
        textAlign: 'left',
        padding: nested
          ? `7px ${theme.space[4]} 7px ${theme.space[8]}`
          : `9px ${theme.space[4]}`,
        background: hover ? 'rgba(255,255,255,0.03)' : 'transparent',
        border: 'none',
        color: hover ? (danger ? theme.semantic.danger : theme.text.primary) : color,
        fontFamily: theme.font.ui,
        fontSize: theme.fontSize.base,
        fontWeight: active ? theme.fontWeight.semibold : theme.fontWeight.medium,
        cursor: 'pointer',
        transition: `all ${theme.motion.fast}`,
      }}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center', color, flexShrink: 0 }}>{icon}</span>}
      <span style={{ flex: 1 }}>{children}</span>
      {rightSlot}
    </button>
  );
}

/* ═══════════════ VIEW AS CLIENT BANNER ═══════════════ */

function ClientViewBanner() {
  const { viewAsClient, setViewAsClient, setMode } = useAdmin();
  if (!viewAsClient) return null;
  return (
    <div style={{
      background: theme.brand.cyanSoft,
      borderBottom: `1px solid ${theme.semantic.infoBorder}`,
      padding: `${theme.space[2]} ${theme.space[4]}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: theme.space[4],
      fontSize: theme.fontSize.sm,
      fontFamily: theme.font.ui,
    }}>
      <span style={{ color: theme.brand.cyan }}>
        <Icon.Eye size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        Viewing as: <strong style={{ color: theme.text.primary }}>{viewAsClient}</strong>
      </span>
      <button
        onClick={() => { setViewAsClient(null); setMode('admin'); }}
        style={{
          background: 'transparent',
          border: `1px solid ${theme.semantic.infoBorder}`,
          borderRadius: theme.radius.sm,
          color: theme.brand.cyan,
          padding: '3px 10px',
          cursor: 'pointer',
          fontSize: theme.fontSize.xs,
          fontFamily: theme.font.ui,
          fontWeight: theme.fontWeight.medium,
          transition: `all ${theme.motion.fast}`,
        }}
      >
        Exit client view
      </button>
    </div>
  );
}

/* ═══════════════ APP LAYOUT ═══════════════ */

// Routes whose nav item is adminOnly — non-admin users should not be parked here.
// When a super-admin switches to Demo mode or View as Client while on one of
// these routes, redirect them to /dashboard so they don't get stuck on a page
// with no corresponding sidebar entry.
const ADMIN_ONLY_ROUTES = [
  '/upload',
  '/admin-tools',
  '/clients',
  '/client-records',
  '/demo-requests',
  '/user-management',
  '/audit-log',
  '/census-database',
];

export function AppLayout() {
  const { isMobile } = useBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { adminMode } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  // Mode-switch redirect: if adminMode just flipped off while the user is on an
  // admin-only route, send them to /dashboard. Using a single effect keyed on
  // both adminMode and pathname means the redirect is idempotent and also covers
  // the initial render case.
  useEffect(() => {
    if (adminMode) return;
    const onAdminRoute = ADMIN_ONLY_ROUTES.some(r => location.pathname.startsWith(r));
    if (onAdminRoute) navigate('/dashboard', { replace: true });
  }, [adminMode, location.pathname, navigate]);

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: theme.surface.base,
      color: theme.text.primary,
      fontFamily: theme.font.ui,
      position: 'relative',
    }}>
      {/* Subtle ambient glow, top right */}
      <div style={{
        position: 'fixed',
        top: -200, right: -200,
        width: 700, height: 700,
        background: `radial-gradient(circle, ${theme.brand.cyanSoft} 0%, transparent 65%)`,
        opacity: 0.5,
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'fixed', top: 12, left: 12, zIndex: 300,
            background: theme.surface.card,
            border: `1px solid ${theme.borderColor.default}`,
            borderRadius: theme.radius.sm,
            padding: 6,
            cursor: 'pointer',
            color: theme.text.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {sidebarOpen ? <Icon.X size={18} /> : <Icon.Menu size={18} />}
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }}
        />
      )}

      <Sidebar
        isMobile={isMobile}
        sidebarOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main style={{
        marginLeft: isMobile ? 0 : SIDEBAR_WIDTH,
        paddingRight: 64,
        flex: 1,
        minHeight: '100vh',
        position: 'relative',
        minWidth: 0,
        zIndex: 1,
      }}>
        <ClientViewBanner />
        <Outlet />
        <Atlas />
      </main>

      <ProfileAvatar />
      <ToastContainer />
    </div>
  );
}
